import React, { useCallback, useMemo, useState, useRef, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import labelStyles from './styles/FormLabel.module.css';
import styles from './styles/FormDate.module.css';

const toDateInputValue = d => {
    if (!d) return '';
    const date = new Date(d);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const addDays = (d, days) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);

const FormDate = ({ label, value, onChange, onAdjustPeer, required = false, id, name, className = '', minDate, maxDate, peerValue, isStart }) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const popoverRef = useRef(null);
    const [popLeft, setPopLeft] = useState(0);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = e => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const EDGE_MARGIN_PX = 10;
        const updatePosition = () => {
            if (!triggerRef.current || !popoverRef.current) return;
            const modalContent = document.querySelector('[data-modal-boundary]');
            const boundaryRect = modalContent ? modalContent.getBoundingClientRect() : { left: 0, right: window.innerWidth || 0 };

            const triggerRect = triggerRef.current.getBoundingClientRect();
            const popWidth = popoverRef.current.offsetWidth || 0;

            // Base left aligns popover's left with trigger's left
            let left = 0; // relative to trigger left
            let popLeftInBoundary = triggerRect.left + left - boundaryRect.left;
            let popRightInBoundary = popLeftInBoundary + popWidth;

            const minLeft = EDGE_MARGIN_PX;
            const maxRight = (boundaryRect.right - boundaryRect.left) - EDGE_MARGIN_PX;

            if (popLeftInBoundary < minLeft) {
                left += (minLeft - popLeftInBoundary);
            }
            popLeftInBoundary = triggerRect.left + left - boundaryRect.left;
            popRightInBoundary = popLeftInBoundary + popWidth;

            if (popRightInBoundary > maxRight) {
                left -= (popRightInBoundary - maxRight);
            }
            setPopLeft(left);
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        return () => window.removeEventListener('resize', updatePosition);
    }, [open]);

    const handleNativeChange = useCallback(e => {
        const next = e.target.value ? new Date(e.target.value) : null;
        if (!next) { if (onChange) onChange(null, e); return; }

        // Enforce min/max
        let adjusted = next;
        if (minDate && adjusted < minDate) adjusted = new Date(minDate);
        if (maxDate && adjusted > maxDate) adjusted = new Date(maxDate);

        // Cross-field validation against peerValue
        if (peerValue) {
            if (isStart && adjusted > peerValue) {
                onChange(adjusted, e);
                if (onAdjustPeer) onAdjustPeer(addDays(adjusted, 1));
            } else if (!isStart && adjusted < peerValue) {
                onChange(adjusted, e);
                if (onAdjustPeer) onAdjustPeer(addDays(adjusted, -1));
            } else {
                onChange(adjusted, e);
            }
        } else {
            onChange(adjusted, e);
        }
    }, [onChange, onAdjustPeer, minDate, maxDate, peerValue, isStart]);

    const monthStart = useMemo(() => new Date((value || new Date()).getFullYear(), (value || new Date()).getMonth(), 1), [value]);
    const [cursor, setCursor] = useState(monthStart);
    const today = useMemo(() => new Date(), []);

    const daysGrid = useMemo(() => {
        const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
        const firstWeekday = start.getDay(); // 0 Sun ... 6 Sat
        const gridStart = addDays(start, -((firstWeekday + 6) % 7)); // start from Monday-like grid
        return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    }, [cursor]);

    const isSameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    const handlePick = useCallback(d => {
        // Enforce min/max
        let adjusted = d;
        if (minDate && adjusted < minDate) adjusted = new Date(minDate);
        if (maxDate && adjusted > maxDate) adjusted = new Date(maxDate);

        // Cross-field validation
        if (peerValue) {
            if (isStart && adjusted > peerValue) {
                onChange(adjusted);
                if (onAdjustPeer) onAdjustPeer(addDays(adjusted, 1));
            } else if (!isStart && adjusted < peerValue) {
                onChange(adjusted);
                if (onAdjustPeer) onAdjustPeer(addDays(adjusted, -1));
            } else {
                onChange(adjusted);
            }
        } else {
            onChange(adjusted);
        }
        setOpen(false);
    }, [onChange, onAdjustPeer, minDate, maxDate, peerValue, isStart]);

    return (
        <div ref={containerRef} className={`${styles.container} ${className}`.trim()}>
            {label && (
                <label className={labelStyles.label} htmlFor={id}>
                    {label}
                    {required && <span className={labelStyles.required}>*</span>}
                </label>
            )}
            <div className={styles.inputButton} ref={triggerRef}>
                <input
                    id={id}
                    name={name}
                    type="text"
                    readOnly
                    onClick={() => setOpen(v => !v)}
                    value={toDateInputValue(value)}
                    className={styles.displayInput}
                />
                {open && (
                    <div className={styles.popover} ref={popoverRef} style={{ left: popLeft }}>
                        <div className={styles.calHeader}>
                            <button type="button" className={styles.navBtn} onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
                                <ChevronLeft size={16} />
                            </button>
                            <div className={styles.monthLabel}>
                                {cursor.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
                            </div>
                            <button type="button" className={styles.navBtn} onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                        <div className={styles.grid}>
                            {['Mo','Tu','We','Th','Fr','Sa','Su'].map(d => (
                                <div key={d} className={styles.dow}>{d}</div>
                            ))}
                            {daysGrid.map((d, idx) => {
                                const outside = d.getMonth() !== cursor.getMonth();
                                const isToday = isSameDay(d, today);
                                const selected = isSameDay(d, value);
                                const disabled = (minDate && d < minDate) || (maxDate && d > maxDate);
                                const cls = [styles.day];
                                if (outside) cls.push(styles.outside);
                                if (isToday) cls.push(styles.today);
                                if (selected) cls.push(styles.selected);
                                if (disabled) cls.push(styles.disabled);
                                return (
                                    <div
                                        key={idx}
                                        className={cls.join(' ')}
                                        onClick={() => !disabled && handlePick(d)}
                                    >
                                        {d.getDate()}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
                <input type="hidden" value={toDateInputValue(value)} onChange={handleNativeChange} />
            </div>
        </div>
    );
};

FormDate.propTypes = {
    label: PropTypes.node,
    value: PropTypes.instanceOf(Date),
    onChange: PropTypes.func,
    required: PropTypes.bool,
    id: PropTypes.string,
    name: PropTypes.string,
    className: PropTypes.string,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    peerValue: PropTypes.instanceOf(Date),
    isStart: PropTypes.bool,
    onAdjustPeer: PropTypes.func,
};

export default memo(FormDate);
